import React, { useCallback, useEffect, useState } from 'react';
import { Button, Switch, Typography } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import {
  acceptAllCookies,
  getConsent,
  hasConsentDecision,
  OPEN_COOKIE_SETTINGS_EVENT,
  rejectAllCookies,
  saveCustomCookies,
} from 'utils/cookieConsent';
import './CookieConsentBanner.css';

const { Text } = Typography;

const CookieConsentBanner = () => {
  const [visible, setVisible] = useState(false);
  const [preferencesOpen, setPreferencesOpen] = useState(false);
  const [analyticalEnabled, setAnalyticalEnabled] = useState(false);

  const syncFromStorage = useCallback(() => {
    const consent = getConsent();
    setAnalyticalEnabled(Boolean(consent?.analytical));
    setVisible(!hasConsentDecision());
  }, []);

  useEffect(() => {
    syncFromStorage();
  }, [syncFromStorage]);

  useEffect(() => {
    const handleOpenSettings = () => {
      const consent = getConsent();
      setAnalyticalEnabled(Boolean(consent?.analytical));
      setPreferencesOpen(true);
      setVisible(false);
    };

    window.addEventListener(OPEN_COOKIE_SETTINGS_EVENT, handleOpenSettings);
    return () => {
      window.removeEventListener(OPEN_COOKIE_SETTINGS_EVENT, handleOpenSettings);
    };
  }, []);

  const handleAcceptAll = () => {
    acceptAllCookies();
    setAnalyticalEnabled(true);
    setVisible(false);
    setPreferencesOpen(false);
  };

  const handleRejectAll = () => {
    rejectAllCookies();
    setAnalyticalEnabled(false);
    setVisible(false);
    setPreferencesOpen(false);
  };

  const handleOpenCustomize = () => {
    const consent = getConsent();
    setAnalyticalEnabled(Boolean(consent?.analytical));
    setVisible(false);
    setPreferencesOpen(true);
  };

  const handleBackFromPreferences = () => {
    setPreferencesOpen(false);
    // Restore the main banner only when the user has not saved a choice yet.
    if (!hasConsentDecision()) {
      setVisible(true);
    }
  };

  const handleSavePreferences = () => {
    saveCustomCookies({ analytical: analyticalEnabled });
    setVisible(false);
    setPreferencesOpen(false);
  };

  const handleCloseBanner = () => {
    // Closing without a choice keeps the banner available next load.
    setVisible(false);
  };

  const policyLink = (
    <a
      className="cookie-consent-card__link"
      href="/#"
      onClick={(event) => event.preventDefault()}
    >
      here
    </a>
  );

  return (
    <>
      {visible && (
        <aside className="cookie-consent-card" role="dialog" aria-label="Cookie settings">
          <div className="cookie-consent-card__header">
            <h2 className="cookie-consent-card__title">Cookie settings</h2>
            <button
              type="button"
              className="cookie-consent-card__close"
              aria-label="Close cookie settings"
              onClick={handleCloseBanner}
            >
              <CloseOutlined />
            </button>
          </div>

          <p className="cookie-consent-card__body">
            We use cookies to deliver our services and, if you agree, to analyze
            site usage so we can improve the product. You can read our Cookie
            Policy {policyLink}.
          </p>

          <p className="cookie-consent-card__note">
            Categories: Essential (always on) and Analytical (optional)
          </p>

          <div className="cookie-consent-card__actions">
            <Button block onClick={handleOpenCustomize}>
              Customize Cookie Settings
            </Button>
            <div className="cookie-consent-card__row">
              <Button block onClick={handleRejectAll}>
                Reject All Cookies
              </Button>
              <Button type="primary" block onClick={handleAcceptAll}>
                Accept All Cookies
              </Button>
            </div>
          </div>
        </aside>
      )}

      {preferencesOpen && (
        <aside className="cookie-consent-card" role="dialog" aria-label="Cookie preferences">
          <div className="cookie-consent-card__header">
            <h2 className="cookie-consent-card__title">Cookie preferences</h2>
            <button
              type="button"
              className="cookie-consent-card__close"
              aria-label="Close cookie preferences"
              onClick={handleBackFromPreferences}
            >
              <CloseOutlined />
            </button>
          </div>

          <Text type="secondary" className="cookie-consent-card__body">
            Choose which cookies you allow. Essential cookies are required.
          </Text>

          <div className="cookie-preferences-row">
            <div>
              <p className="cookie-preferences-row__title">Essential cookies</p>
              <p className="cookie-preferences-row__desc">
                Required for login, security, and saving your cookie choice.
              </p>
            </div>
            <div className="cookie-preferences-row__control">
              <Switch checked disabled />
              <span className="cookie-preferences-row__always-on">Always on</span>
            </div>
          </div>

          <div className="cookie-preferences-row">
            <div>
              <p className="cookie-preferences-row__title">Analytical cookies</p>
              <p className="cookie-preferences-row__desc">
                Help us understand product usage and improve the experience.
              </p>
            </div>
            <div className="cookie-preferences-row__control">
              <Switch
                checked={analyticalEnabled}
                onChange={setAnalyticalEnabled}
              />
            </div>
          </div>

          <div className="cookie-consent-card__actions cookie-consent-card__actions--prefs">
            <div className="cookie-consent-card__row">
              <Button block onClick={handleBackFromPreferences}>
                Back
              </Button>
              <Button type="primary" block onClick={handleSavePreferences}>
                Save preferences
              </Button>
            </div>
          </div>
        </aside>
      )}
    </>
  );
};

export default CookieConsentBanner;
